import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Title,
  Meta,
  WithFeedbackProps,
  withFeedback,
  Paragraph,
  Spacing,
  Button,
  Loader,
  Modal,
} from "@sicredi/react";

import { CardPlan } from "#/components/MoreOffers/CardPlan";
import styles from "./styles.module.scss";
import { ContextAPI } from "#/contextProver/ContextProver";
import { PAGE } from "#/constants";
import { useNavigate } from "react-router-dom";
import { CardPlanSmall } from "#/components/MoreOffers/CardPlanSmall";
import { HeaderCardPlanSmall } from "#/components/MoreOffers/HeaderCardPlanSmall";
import { getVendaResumo } from "#/api/getVendaResumo";
import {
  getListaPaginacaoEscolhaPlano,
  getTotalPaginas,
  getValorDecodeBase64,
  toastErrorMenssage,
  getPercentualTaxaAdm,
  loadSessionStorage,
} from "#/contextProver/ContextProverServices";
import { getVendasPlanosCobranca } from "#/api/getVendasPlanosCobranca";
import { getCadastroAtualizar } from "#/api/getCadastroAtualizar";
import { FEED, getFeedBackAssociados } from "#/api/associadosFeedBackLogin";

export const ChoiceOfPlan: React.FC<WithFeedbackProps> = ({
  setError,
  setSucceeded,
  setException,
  clearFeedback,
}) => {
  const {
    isMobile,
    codigoComissionado,
    setVendasResumo,
    setIdSimulacao,
    seguro,
    documento,
    setVendasPlanosCobranca,
    isLoading,
    setIsLoading,
    bens,
  } = useContext(ContextAPI);
  const navigate = useNavigate();
  const moreOffersRef = useRef<HTMLDivElement>(null);
  const [showMoreOffers, setShowMoreOffers] = useState(false);
  const [atualPagina, setAtualPagina] = useState(1);
  const quantidadePorPagina = 3;
  const [idPessoa, setIdPessoa] = useState<string | null>(null);
  const [idContaBancaria, setIdContaBancaria] = useState<string | null>(null);
  const [loadingCadastro, setLoadingCadastro] = useState<boolean>(true);
  const [isOpenIndisponivelModal, setIsOpenIndisponivelModal] =
    useState(false);
  const [isOpenMenorIdadeModal, setIsOpenMenorIdadeModal] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchCadastroMinimo = async () => {
      try {
        setLoadingCadastro(true);
        const isHasAuthContaStorage = loadSessionStorage("isHasAuthConta");
        if (!isHasAuthContaStorage || !documento) {
          if (mounted) setLoadingCadastro(false);
          return;
        }

        const conta = getValorDecodeBase64(isHasAuthContaStorage.conta);
        const cooperativa = getValorDecodeBase64(isHasAuthContaStorage.agencia);

        const cadastro = await getCadastroAtualizar({
          conta,
          coop: cooperativa,
          documento,
        });

        if (!mounted) return;

        setIdPessoa(cadastro?.data?.id_pessoa ?? null);
        setIdContaBancaria(cadastro?.data?.id_conta_bancaria ?? null);
        setLoadingCadastro(false);
      } catch (e) {
        if (!mounted) return;
        setLoadingCadastro(false);
        toastErrorMenssage("Não foi possível obter o cadastro. Tente novamente.");
      }
    };

    fetchCadastroMinimo();
    return () => {
      mounted = false;
    };
  }, [documento]);

  const propostasNaoRepetidas = bens?.data?.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (t) =>
          t.valorParcela === item.valorParcela &&
          t.prazoRestante === item.prazoRestante
      )
  );
  const principaisOfertas = propostasNaoRepetidas?.slice(
    0,
    quantidadePorPagina
  );
  const maisOfertas = propostasNaoRepetidas
    ? propostasNaoRepetidas?.slice(quantidadePorPagina)
    : undefined;

  const paginacaoLista = getListaPaginacaoEscolhaPlano({
    apiLista: maisOfertas,
    atualPagina,
    quantidadePorPagina,
  });

  const totalPaginas = getTotalPaginas({
    quantidadeLista: maisOfertas?.length,
    quantidadePorPagina,
  });

  const handleContratar = async (
    idSimulacao: number,
    possivelContratar: boolean
  ) => {
    try {
      setIsLoading(true);

      if (!possivelContratar) {
        setIsLoading(false);
        setIsOpenIndisponivelModal(true);
        return;
      }

      const isHasAuthContaStorage = loadSessionStorage("isHasAuthConta");
      const conta = getValorDecodeBase64(isHasAuthContaStorage?.conta);
      const cooperativa = getValorDecodeBase64(
        isHasAuthContaStorage?.agencia
      );

      const menorIdade = await getFeedBackAssociados({
        conta,
        cooperativa,
        codigoComissionado,
        documento,
        accountFeedback: FEED.MENOR_IDADE,
      });
      if (menorIdade.data.feedback != null) {
        setIsLoading(false);
        setIsOpenMenorIdadeModal(true);
        return;
      }

      if (!idPessoa || !idContaBancaria) {
        setIsLoading(false);
        toastErrorMenssage(
          "Dados do cadastro indisponíveis. Tente novamente."
        );
        return;
      }

      const vendasResumo = await getVendaResumo({
        codigoComissionado,
        idPessoa: Number(idPessoa),
        idSimulacao,
        idContaBancaria: idContaBancaria,
        documento,
      });
      setVendasResumo(vendasResumo);

      const vendasPlanosCobranca = await getVendasPlanosCobranca({
        codigoComissionado,
        idSimulacao,
        seguro,
      });
      setVendasPlanosCobranca(vendasPlanosCobranca);

      setIdSimulacao(idSimulacao);
      navigate(PAGE.RESUME);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
      toastErrorMenssage();
    }
  };

  const handleMoreOffers = () => {
    setShowMoreOffers(!showMoreOffers);

    if (!showMoreOffers) {
      setTimeout(() => {
        moreOffersRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  useEffect(() => {
    if (bens?.data?.length === 0) {
      navigate(PAGE.ROOT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      <Meta title="Home" />
      <Loader show={isLoading || loadingCadastro} fullScreen />
      <div className={styles.content}>
        <Title
          as="h1"
          size="xxx-large"
          weight="semi-bold"
          className={styles.title}
        >
          Escolha o plano
        </Title>

        <Spacing appearance="small" />

        <Paragraph className={styles.paragraph}>
          Oportunidades que se encaixam na sua expectativa de parcela e valor de
          crédito.
        </Paragraph>

        <Spacing appearance="large" />

        <div className={styles.content_offers}>
          {principaisOfertas?.map((item, index) => {
            return (
              <div key={index}>
                <CardPlan
                  key={index}
                  valorBem={item.valorBem}
                  valorParcela={item.valorParcela}
                  prazoRestante={item.prazoRestante}
                  percentualTaxaAdm={getPercentualTaxaAdm(
                    item.percentualTaxaAdm,
                    item.prazoRestante
                  )}
                  possivelContratar={item.possivelContratar}
                  melhorProposta={item.melhorProposta}
                  showButtonContract={true}
                  onClickContratar={async () =>
                    await handleContratar(
                      item.idSimulacao,
                      item.possivelContratar
                    )
                  }
                />
              </div>
            );
          })}
        </div>

        <Spacing appearance="large" />
        <div className={styles.button_container}>
          <Button
            appearance="secondary"
            className={styles.button}
            size={isMobile ? undefined : "large"}
            onClick={() => navigate(PAGE.ROOT)}
          >
            Nova simulação
          </Button>

          {propostasNaoRepetidas?.length > quantidadePorPagina && (
            <Button
              appearance="primary"
              className={styles.button}
              size={isMobile ? undefined : "large"}
              onClick={handleMoreOffers}
            >
              Mais ofertas
            </Button>
          )}
        </div>
        <Spacing appearance="x-large" />

        {showMoreOffers && (
          <>
            <Spacing appearance="small" />

            {isMobile ? (
              <>
                <div className={styles.content_offers} ref={moreOffersRef}>
                  {paginacaoLista?.map((item, index) => {
                    return (
                      <div key={index}>
                        <CardPlan
                          valorBem={item.valorBem}
                          valorParcela={item.valorParcela}
                          prazoRestante={item.prazoRestante}
                          percentualTaxaAdm={getPercentualTaxaAdm(
                            item.percentualTaxaAdm,
                            item.prazoRestante
                          )}
                          possivelContratar={item.possivelContratar}
                          melhorProposta={item.melhorProposta}
                          showButtonContract={true}
                          onClickContratar={async () =>
                            await handleContratar(
                              item.idSimulacao,
                              item.possivelContratar
                            )
                          }
                        />
                      </div>
                    );
                  })}
                </div>
                <Spacing appearance="small" />
              </>
            ) : (
              <>
                <HeaderCardPlanSmall />

                {paginacaoLista?.map((item, index) => {
                  return (
                    <div key={index}>
                      <CardPlanSmall
                        key={index}
                        valorBem={item.valorBem}
                        valorParcela={item.valorParcela}
                        prazoRestante={item.prazoRestante}
                        percentualTaxaAdm={getPercentualTaxaAdm(
                          item.percentualTaxaAdm,
                          item.prazoRestante
                        )}
                        onClickContratar={async () =>
                          await handleContratar(
                            item.idSimulacao,
                            item.possivelContratar
                          )
                        }
                      />
                    </div>
                  );
                })}
              </>
            )}

            <div className={styles.button_load_container}>
              {atualPagina !== totalPaginas && (
                <Button
                  onClick={() => setAtualPagina(atualPagina + 1)}
                  className={styles.button_load}
                  appearance="secondary"
                >
                  Carregar mais ofertas
                </Button>
              )}
            </div>
          </>
        )}
        <Spacing appearance="large" />

        <Modal
          title="Indisponível para contratação"
          show={isOpenIndisponivelModal}
          size="small"
          onClose={() => setIsOpenIndisponivelModal(false)}
          primaryButton={{
            children: "Entendi",
            onClick: () => setIsOpenIndisponivelModal(false),
          }}
        >
          Não foi possível seguir com esta opção no momento. Procure sua
          agência para atualizar suas informações e verificar as
          possibilidades de contratação.
        </Modal>

        <Modal
          title="Idade abaixo do permitido"
          show={isOpenMenorIdadeModal}
          size="small"
          onClose={() => setIsOpenMenorIdadeModal(false)}
          primaryButton={{
            children: "Entendi",
            onClick: () => setIsOpenMenorIdadeModal(false),
          }}
        >
          É preciso ter 18 anos ou mais para contratar um consórcio.
        </Modal>
      </div>
    </div>
  );
};

export default withFeedback(ChoiceOfPlan);
